import React, { useState, useEffect, useMemo } from 'react';
import { toLocalYMD } from '../lib/dates';
import {
  Users, ChevronLeft, ChevronRight, Palmtree, Stethoscope, Clock,
  Plus, Trash2, Loader2, Calendar, X, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAVE_TYPES = [
  { id: 'annual', label: 'Annual Leave', icon: Palmtree, color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-100' },
  { id: 'sick', label: 'Sick Leave', icon: Stethoscope, color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-100' },
  { id: 'other', label: 'Other', icon: Clock, color: 'bg-zinc-500', textColor: 'text-zinc-600', bgLight: 'bg-zinc-200' },
];

// Get leave type styling
const getLeaveTypeInfo = (type) => {
  return LEAVE_TYPES.find(t => t.id === type) || LEAVE_TYPES[2];
};

// Generate calendar data for a month
const generateMonthCalendar = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday
  
  // Adjust for Monday start (0 = Monday, 6 = Sunday)
  const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
  
  const weeks = [];
  let currentWeek = Array(7).fill(null);
  
  for (let day = 1; day <= daysInMonth; day++) {
    const dayIndex = (adjustedStart + day - 1) % 7;
    const weekIndex = Math.floor((adjustedStart + day - 1) / 7);
    
    if (!weeks[weekIndex]) {
      weeks[weekIndex] = Array(7).fill(null);
    }
    
    weeks[weekIndex][dayIndex] = day;
  }
  
  return weeks;
};

// Month names
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 
                'July', 'August', 'September', 'October', 'November', 'December'];

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Single Month Calendar Component
const MonthCalendar = ({ year, month, leaveDates, onDayClick, selectedDates }) => {
  const weeks = useMemo(() => generateMonthCalendar(year, month), [year, month]);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  
  return (
    <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm">
      {/* Month header */}
      <div className="bg-zinc-50 px-3 py-2 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-800">{MONTHS[month]}</h3>
      </div>
      
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-zinc-100">
        {DAY_HEADERS.map(day => (
          <div key={day} className="text-center py-1 text-[10px] font-medium text-zinc-500">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="p-1">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} className="grid grid-cols-7 gap-px">
            {week.map((day, dayIdx) => {
              if (!day) {
                return <div key={dayIdx} className="aspect-square" />;
              }
              
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const leaveInfo = leaveDates[dateStr];
              const isToday = isCurrentMonth && today.getDate() === day;
              const isSelected = selectedDates.includes(dateStr);
              const isPast = new Date(dateStr) < new Date(today.toDateString());
              
              return (
                <button
                  key={dayIdx}
                  onClick={() => !isPast && onDayClick(dateStr)}
                  disabled={isPast}
                  data-testid={`day-${dateStr}`}
                  className={`
                    aspect-square flex items-center justify-center rounded-md text-xs relative
                    transition-all duration-150
                    ${isPast ? 'text-zinc-300 cursor-not-allowed' : 'hover:bg-zinc-100 cursor-pointer'}
                    ${isToday ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}
                    ${isSelected ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}
                    ${leaveInfo && !isSelected ? getLeaveTypeInfo(leaveInfo.leave_type).bgLight : ''}
                  `}
                >
                  <span className={`
                    ${leaveInfo && !isSelected ? getLeaveTypeInfo(leaveInfo.leave_type).textColor + ' font-semibold' : ''}
                  `}>
                    {day}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// Add Leave Modal
const AddLeaveModal = ({ isOpen, onClose, workers, selectedDates, onSubmit, loading }) => {
  const [selectedWorker, setSelectedWorker] = useState('');
  const [leaveType, setLeaveType] = useState('annual');
  const [notes, setNotes] = useState('');
  
  if (!isOpen) return null;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedWorker) {
      toast.error('Please select a worker');
      return;
    }
    onSubmit({ workerId: selectedWorker, leaveType, notes });
  };
  
  const sortedDates = [...selectedDates].sort();
  const startDate = sortedDates[0];
  const endDate = sortedDates[sortedDates.length - 1];
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Palmtree size={20} className="text-white" />
            </div>
            <h2 className="text-lg font-semibold text-white">Book Leave</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            data-testid="close-leave-modal"
          >
            <X size={20} className="text-white" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Selected dates display */}
          <div className="p-3 bg-zinc-50 rounded-xl">
            <p className="text-sm text-zinc-600">
              <span className="font-medium">Selected dates:</span>{' '}
              {sortedDates.length === 1 ? (
                new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
              ) : (
                `${new Date(startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} (${sortedDates.length} days)`
              )}
            </p>
          </div>
          
          {/* Worker Selection */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Worker</label>
            <select
              value={selectedWorker}
              onChange={(e) => setSelectedWorker(e.target.value)}
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="worker-select"
              required
            >
              <option value="">Select a worker...</option>
              {workers.map(worker => (
                <option key={worker.id} value={worker.id}>{worker.name}</option>
              ))}
            </select>
          </div>
          
          {/* Leave Type */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">Leave Type</label>
            <div className="grid grid-cols-3 gap-2">
              {LEAVE_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setLeaveType(type.id)}
                    data-testid={`leave-type-${type.id}`}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                      leaveType === type.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${type.color}`}>
                      <Icon size={16} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-zinc-700">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Family holiday, Doctor's appointment..."
              className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="leave-notes"
            />
          </div>
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            data-testid="submit-leave"
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-zinc-300 text-white rounded-xl font-medium transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Booking...
              </>
            ) : (
              <>
                <Plus size={18} />
                Book Leave
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Leave List Item
const LeaveItem = ({ leave, onDelete, deleting }) => {
  const typeInfo = getLeaveTypeInfo(leave.leave_type);
  const Icon = typeInfo.icon;
  
  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options = { day: 'numeric', month: 'short' };
    
    if (start === end) {
      return startDate.toLocaleDateString('en-GB', { ...options, year: 'numeric' });
    }
    return `${startDate.toLocaleDateString('en-GB', options)} - ${endDate.toLocaleDateString('en-GB', { ...options, year: 'numeric' })}`;
  };
  
  return (
    <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-zinc-200 hover:border-zinc-300 transition-colors">
      <div className={`p-2 rounded-lg ${typeInfo.color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-zinc-900">{leave.worker_name}</p>
        <p className="text-sm text-zinc-500">{formatDateRange(leave.start_date, leave.end_date)}</p>
        {leave.notes && (
          <p className="text-xs text-zinc-400 mt-0.5 truncate">{leave.notes}</p>
        )}
      </div>
      <button
        onClick={() => onDelete(leave.id, leave.worker_name)}
        disabled={deleting === leave.id}
        data-testid={`delete-leave-${leave.id}`}
        className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
        title="Cancel leave"
      >
        {deleting === leave.id ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          <Trash2 size={16} />
        )}
      </button>
    </div>
  );
};

// Main Staff Leave Page Component
const StaffLeave = ({ onBack }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedWorker, setSelectedWorker] = useState('all');
  const [workers, setWorkers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Fetch workers
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const response = await fetch(`${API_URL}/api/team/members`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Filter to just workers/team members
          const workerList = (data.members || data || [])
            .filter(m => m.role === 'Worker' || m.role === 'Planner' || m.type === 'worker')
            .map(m => ({ id: m.id || m._id, name: m.name || m.email }));
          
          // If no workers found from API, show empty state - do NOT use hardcoded fallback
          if (workerList.length === 0) {
            setWorkers([]);
          } else {
            setWorkers(workerList);
          }
        } else {
          // No workers
          setWorkers([]);
        }
      } catch (err) {
        console.error('Error fetching workers:', err);
        setWorkers([]);
      }
    };
    
    fetchWorkers();
  }, []);
  
  // Fetch leaves for the year
  useEffect(() => {
    const fetchLeaves = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        const startDate = `${selectedYear}-01-01`;
        const endDate = `${selectedYear}-12-31`;
        
        let url = `${API_URL}/api/holidays/leave?start_date=${startDate}&end_date=${endDate}`;
        if (selectedWorker !== 'all') {
          url += `&worker_id=${selectedWorker}`;
        }
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setLeaves(data.leaves || []);
        }
      } catch (err) {
        console.error('Error fetching leaves:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaves();
  }, [selectedYear, selectedWorker]);
  
  // Build leave dates map for calendar highlighting
  const leaveDates = useMemo(() => {
    const dateMap = {};
    leaves.forEach(leave => {
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateStr = toLocalYMD(d);
        dateMap[dateStr] = {
          leave_type: leave.leave_type,
          worker_name: leave.worker_name,
          worker_id: leave.worker_id
        };
      }
    });
    return dateMap;
  }, [leaves]);
  
  // Handle day click for date selection
  const handleDayClick = (dateStr) => {
    setSelectedDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      }
      return [...prev, dateStr];
    });
  };
  
  // Handle leave submission
  const handleSubmitLeave = async ({ workerId, leaveType, notes }) => {
    if (selectedDates.length === 0) return;
    
    const worker = workers.find(w => w.id === workerId);
    if (!worker) return;
    
    const sortedDates = [...selectedDates].sort();
    const startDate = sortedDates[0];
    const endDate = sortedDates[sortedDates.length - 1];
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/holidays/leave`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          worker_id: workerId,
          worker_name: worker.name,
          start_date: startDate,
          end_date: endDate,
          leave_type: leaveType,
          notes: notes || null
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to book leave');
      }
      
      toast.success(`Leave booked for ${worker.name}`);
      setShowAddModal(false);
      setSelectedDates([]);
      
      // Refresh leaves
      const refreshResponse = await fetch(
        `${API_URL}/api/holidays/leave?start_date=${selectedYear}-01-01&end_date=${selectedYear}-12-31${selectedWorker !== 'all' ? `&worker_id=${selectedWorker}` : ''}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (refreshResponse.ok) {
        const data = await refreshResponse.json();
        setLeaves(data.leaves || []);
      }
      
    } catch (err) {
      console.error('Error booking leave:', err);
      toast.error(err.message || 'Failed to book leave');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle leave deletion
  const handleDeleteLeave = async (leaveId, workerName) => {
    if (!confirm(`Cancel leave for ${workerName}?`)) return;
    
    setDeleting(leaveId);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/holidays/leave/${leaveId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete leave');
      }
      
      toast.success('Leave cancelled');
      setLeaves(prev => prev.filter(l => l.id !== leaveId));
      
    } catch (err) {
      console.error('Error deleting leave:', err);
      toast.error('Failed to delete leave');
    } finally {
      setDeleting(null);
    }
  };
  
  // Calculate stats
  const leaveStats = useMemo(() => {
    const stats = {
      total: leaves.length,
      annual: leaves.filter(l => l.leave_type === 'annual').length,
      sick: leaves.filter(l => l.leave_type === 'sick').length,
      other: leaves.filter(l => l.leave_type === 'other').length,
    };
    return stats;
  }, [leaves]);
  
  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="staff-leave-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ChevronLeft size={20} className="text-zinc-600" />
              </button>
            )}
            <div className="p-2 bg-emerald-100 rounded-xl">
              <Users size={24} className="text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Staff Leave</h1>
              <p className="text-sm text-zinc-500">Manage worker holidays and time off</p>
            </div>
          </div>
          
          {/* Book Leave Button - appears when dates selected */}
          {selectedDates.length > 0 && (
            <button
              onClick={() => setShowAddModal(true)}
              data-testid="book-leave-btn"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors"
            >
              <Plus size={18} />
              Book Leave ({selectedDates.length} {selectedDates.length === 1 ? 'day' : 'days'})
            </button>
          )}
        </div>
        
        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          {/* Year selector */}
          <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
            <button
              onClick={() => setSelectedYear(y => y - 1)}
              className="p-2 hover:bg-zinc-200 rounded-md transition-colors"
              data-testid="prev-year"
            >
              <ChevronLeft size={16} className="text-zinc-600" />
            </button>
            <span className="px-3 font-semibold text-zinc-800">{selectedYear}</span>
            <button
              onClick={() => setSelectedYear(y => y + 1)}
              className="p-2 hover:bg-zinc-200 rounded-md transition-colors"
              data-testid="next-year"
            >
              <ChevronRight size={16} className="text-zinc-600" />
            </button>
          </div>
          
          {/* Worker filter */}
          <select
            value={selectedWorker}
            onChange={(e) => setSelectedWorker(e.target.value)}
            className="px-4 py-2 border border-zinc-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            data-testid="worker-filter"
          >
            <option value="all">All Workers</option>
            {workers.map(worker => (
              <option key={worker.id} value={worker.id}>{worker.name}</option>
            ))}
          </select>
          
          {/* Leave type legend */}
          <div className="flex items-center gap-3 ml-auto text-xs">
            {LEAVE_TYPES.map(type => (
              <div key={type.id} className="flex items-center gap-1.5">
                <div className={`w-3 h-3 rounded ${type.color}`} />
                <span className="text-zinc-600">{type.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Selection hint */}
        {selectedDates.length === 0 && (
          <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1">
            <AlertCircle size={12} />
            Click on calendar dates to select days for booking leave
          </p>
        )}
      </div>
      
      {/* Main content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={32} className="animate-spin text-emerald-500" />
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4">
            {/* 12-Month Calendar Grid */}
            <div className="flex-1">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 12 }, (_, i) => (
                  <MonthCalendar
                    key={i}
                    year={selectedYear}
                    month={i}
                    leaveDates={leaveDates}
                    onDayClick={handleDayClick}
                    selectedDates={selectedDates}
                  />
                ))}
              </div>
            </div>
            
            {/* Leave List Sidebar */}
            <div className="lg:w-80 space-y-4">
              {/* Stats */}
              <div className="bg-white rounded-xl border border-zinc-200 p-4">
                <h3 className="text-sm font-semibold text-zinc-800 mb-3">
                  {selectedYear} Leave Summary
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-zinc-50 rounded-lg">
                    <p className="text-2xl font-bold text-zinc-900">{leaveStats.total}</p>
                    <p className="text-xs text-zinc-500">Total Bookings</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{leaveStats.annual}</p>
                    <p className="text-xs text-zinc-500">Annual Leave</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{leaveStats.sick}</p>
                    <p className="text-xs text-zinc-500">Sick Days</p>
                  </div>
                  <div className="text-center p-3 bg-zinc-100 rounded-lg">
                    <p className="text-2xl font-bold text-zinc-600">{leaveStats.other}</p>
                    <p className="text-xs text-zinc-500">Other</p>
                  </div>
                </div>
              </div>
              
              {/* Leave list */}
              <div className="bg-white rounded-xl border border-zinc-200 p-4">
                <h3 className="text-sm font-semibold text-zinc-800 mb-3">
                  Upcoming & Recent Leave
                </h3>
                {leaves.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar size={40} className="mx-auto mb-3 text-zinc-300" />
                    <p className="text-zinc-600 font-medium">No leave booked</p>
                    <p className="text-xs text-zinc-400 mt-1">Select dates on the calendar to book time off</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {leaves.slice(0, 20).map(leave => (
                      <LeaveItem
                        key={leave.id}
                        leave={leave}
                        onDelete={handleDeleteLeave}
                        deleting={deleting}
                      />
                    ))}
                    {leaves.length > 20 && (
                      <p className="text-xs text-zinc-500 text-center py-2">
                        + {leaves.length - 20} more bookings
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Add Leave Modal */}
      <AddLeaveModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedDates([]);
        }}
        workers={workers}
        selectedDates={selectedDates}
        onSubmit={handleSubmitLeave}
        loading={submitting}
      />
    </div>
  );
};

export default StaffLeave;
